import { capitalise, cn } from '@fortem-monorepo/js-utilities'
import { ICG2Guidelines } from '@fortem-monorepo/web-sdk'

import { formatGuidelineNumber } from '@/pages/funds/ic-guidelines-v2/components/stages/breaches-stage/format-guideline-number'

export interface GuidelineRowThresholdRangeProps {
  thresholds: ICG2Guidelines.GuidelineThresholds
  thresholdType: ICG2Guidelines.ThresholdType
  values: number[]
}

export const GuidelineRowThresholdRange: React.FC<GuidelineRowThresholdRangeProps> = ({
  thresholds,
  thresholdType,
  values,
}) => {
  const { hard_lower, soft_lower, soft_upper, hard_upper } = thresholds

  const points = [
    ...Object.entries(thresholds).map(([key, value]) => ({ type: key, value })),
    ...values.map((value) => ({ type: 'value', value })),
  ].filter((point): point is Point => point.value !== null && point.value !== undefined)

  if (!points.length) return null

  const thresholdValues = points
    .filter((point) => point.type !== 'value')
    .map((point) => point.value)
  const valueNumbers = points.filter((point) => point.type === 'value').map((point) => point.value)
  const { domainMin, domainMax } = getDomainBounds(thresholdValues, valueNumbers)

  const domainSpan = domainMax - domainMin || 1

  const toPercent = (value: number) => {
    return ((value - domainMin) / domainSpan) * 100
  }

  type Segment = {
    from: number
    to: number
    status: 'hard' | 'soft' | 'ok'
  }

  const lowerSegments: Segment[] =
    hard_lower !== null && soft_lower !== null
      ? [
          { from: domainMin, to: hard_lower, status: 'hard' },
          { from: hard_lower, to: soft_lower, status: 'soft' },
        ]
      : soft_lower !== null
        ? [{ from: domainMin, to: soft_lower, status: 'soft' }]
        : hard_lower !== null
          ? [{ from: domainMin, to: hard_lower, status: 'hard' }]
          : []

  const okFrom = soft_lower ?? hard_lower ?? domainMin
  const okTo = soft_upper ?? hard_upper ?? domainMax

  const okSegments: Segment[] = okTo > okFrom ? [{ from: okFrom, to: okTo, status: 'ok' }] : []

  const upperSegments: Segment[] =
    soft_upper !== null && hard_upper !== null
      ? [
          { from: soft_upper, to: hard_upper, status: 'soft' },
          { from: hard_upper, to: domainMax, status: 'hard' },
        ]
      : soft_upper !== null
        ? [{ from: soft_upper, to: domainMax, status: 'soft' }]
        : hard_upper !== null
          ? [{ from: hard_upper, to: domainMax, status: 'hard' }]
          : []

  const segments = [...lowerSegments, ...okSegments, ...upperSegments].filter(
    (segment) => segment.to > segment.from,
  )

  return (
    <div className={'p-6 pb-14 bg-gray-100'}>
      <span className={'tracking-wider text-muted-foreground text-xs'}>DISTRIBUTION</span>
      <div className="relative w-full h-2 mt-6">
        <div className="overflow-hidden rounded-full bg-slate-200">
          {segments.map((segment, index) => (
            <div
              key={index}
              className={cn('absolute top-0 h-full', {
                'bg-red-300': segment.status === 'hard',
                'bg-amber-300': segment.status === 'soft',
                'bg-emerald-300': segment.status === 'ok',
              })}
              style={{
                left: `${toPercent(segment.from)}%`,
                width: `${toPercent(segment.to) - toPercent(segment.from)}%`,
              }}
            />
          ))}
          {points.map((point, index) => (
            <div
              key={`${point.type}-${point.value}-${index}`}
              className={cn(
                'absolute left-0 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 rounded-full border shadow-sm',
                {
                  'w-px h-3': point.type !== 'value', // Threshold markers
                  'border-slate-700 bg-slate-500 size-3': point.type === 'value', // Value Markets
                  'border-red-600 bg-red-500': ['hard_lower', 'hard_upper'].includes(point.type),
                  'border-amber-600 bg-amber-500': ['soft_lower', 'soft_upper'].includes(
                    point.type,
                  ),
                },
              )}
              style={{ left: `${toPercent(point.value)}%` }}
            >
              {point.type !== 'value' && (
                <div
                  className={
                    'absolute top-6 -translate-x-1/2 inline-flex gap-1 text-muted-foreground uppercase tracking-wider text-xs whitespace-nowrap'
                  }
                >
                  <span className={''}>{capitalise(point.type.replaceAll('_', ' '))}</span>
                  <span className={'font-mono font-semibold'}>
                    {formatGuidelineNumber(point.value, thresholdType)}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

type Point = {
  value: number
  type: 'value' | keyof ICG2Guidelines.GuidelineThresholds
}

const getDomainBounds = (thresholdValues: number[], valueNumbers: number[]) => {
  if (!thresholdValues.length) {
    return withPadding(Math.min(...valueNumbers), Math.max(...valueNumbers))
  }

  let rawMin = Math.min(...thresholdValues)
  let rawMax = Math.max(...thresholdValues)

  if (rawMin === rawMax) {
    const threshold = rawMin
    const valueDistance = valueNumbers.length
      ? Math.max(...valueNumbers.map((value) => Math.abs(value - threshold)))
      : 0
    const context = Math.max(valueDistance, Math.abs(threshold || 1) * 0.25, 1)

    rawMin = threshold - context
    rawMax = threshold + context

    if (threshold >= 0 && valueNumbers.every((value) => value >= 0)) {
      rawMin = Math.min(0, rawMin)
    }
  }

  if (valueNumbers.length) {
    rawMin = Math.min(rawMin, ...valueNumbers)
    rawMax = Math.max(rawMax, ...valueNumbers)
  }

  return withPadding(rawMin, rawMax)
}

const withPadding = (rawMin: number, rawMax: number) => {
  const rawSpan = rawMax - rawMin
  const padding = rawSpan === 0 ? Math.abs(rawMin || 1) * 0.08 : rawSpan * 0.08

  return {
    domainMin: rawMin - padding,
    domainMax: rawMax + padding,
  }
}
